/* global Resources, Sys, Services, Utils */
Sys.ns("Services");

/**
 * The currency format.
 *
 * @typedef {Object} CurrencyFormat
 * @property {string} iso - The ISO code.
 * @property {number} decimals - The number of decimals.
 * @property {string} decimalDivider - The decimal divider.
 * @property {string} thousandsDivider - The thousand divider.
 */

/**
 * The Money Service.
 *
 * @class Services.Money
 * @extends Services.Base
 */
Services.Money = {
    // define service constants.
    MONEY_TYPES: {
        CASH: "cash",
        COINS: "coins"
    },

    DEFAULT_MONEY_TYPES: ["cash", "coins"],

    constructor: function(config) {
        Services.Money.superclass.constructor.apply(this, arguments);

        this._setupData(config);
    },

    _setupEvents: function() {
        this.observable.on({
            "notify:moneyManager.balanceChanged": this._balanceChanged.bind(this)
        });
    },

    /**
     * Setup data for the service.
     *
     * @param {Object} config Service configuration object.
     * @param {Array<"cash" | "coins">} config.defaultBettingModes Provided default betting modes value.
     * @returns {void}
     */
    _setupData: function(config) {
        var initResponse = Resources.readData("gameServerInitResponseObject"),
            urlParameters = Sys.utils.parseUrlParams(window.location.search);

        this._setDefaultBettingModes(config.defaultBettingModes);

        this._setupMoneyInfo(urlParameters, initResponse);

        if (this._types.length === 1) {
            // If only single MoneyType is supported then activate that type and force update "isCash" setting in local storage.
            this._type = this._types[0];
        }
        else {
            // Set initial moneytype as per "isCash" setting value in local storage.
            switch (Services.localStorageManager.readData("bettingModes")) {
                case this.MONEY_TYPES.CASH:
                    this._type = this.MONEY_TYPES.CASH;
                    break;
                case this.MONEY_TYPES.COINS:
                    this._type = this.MONEY_TYPES.COINS;
                    break;
                default:
                    this._type = this._types[0];
            }
        }
    },

    /**
     * Validates and sets available default betting modes.
     *
     * @param {Array<"cash" | "coins">} bettingModes Provided value of bettingModes.
     * @private
     * @returns {void}
     */
    _setDefaultBettingModes: function(bettingModes) {
        bettingModes =
            typeof bettingModes !== "undefined" ?
                bettingModes :
                this.DEFAULT_MONEY_TYPES;
        if (
            !Array.isArray(bettingModes) ||
            !this._isValidBettingModes(bettingModes)
        ) {
            this.observable.fireEvent(
                "request:loaderErrorHandler.showTechnicalError"
            );
            throw new Error("Provided invalid betting mode!");
        }

        this._defaultMoneyTypes = bettingModes;
    },

    /**
     * Gets the players balance in cash.
     *
     * @returns {number} The players balance in cents.
     */
    getBalanceCash: function() {
        return Services.moneyManager.getBalanceCents();
    },

    /**
     * Gets the players balance in coins.
     *
     * @returns {number} The players balance in coins.
     */
    getBalanceCoins: function() {
        return Services.moneyManager.getBalanceCoins();
    },

    /**
     * Gets the currency ISO text.
     *
     * @returns {string} The currency ISO text (e.g. EUR, SEK).
     */
    getCurrencyISO: function() {
        return Services.moneyManager.getCurrencyIsoName();
    },

    /**
     * Gets the current money type.
     *
     * @returns {string} The current money type ("cash", "coins").
     */
    getType: function() {
        return this._type;
    },

    /**
     * Gets the available money types.
     *
     * @returns {string[]} The available money types.
     */
    getTypes: function() {
        return this._types;
    },

    /**
     * Sets the current money type.
     *
     * @param {string} type The money type.
     * @throws If the money type is not one of the available types.
     * @returns {void}
     */
    setType: function(type) {
        if (this._types.indexOf(type) === -1) {
            throw new Error("Invalid money type");
        }

        if (this._type !== type) {
            Utils.Helpers.logPlayerInteracted("moneyModeChanged", this._type, type);

            Services.localStorageManager.storeData("bettingModes", type);

            this._type = type;

            this.publish("typeChanged", type);
        }
    },

    /**
     * Sets available money types. Should be used in case game is not supporting both cash and coins.
     *
     * @param {Array<string>} types The money types.
     * @returns {void}
     */
    setTypes: function(types) {
        if (!Array.isArray(types) || !this._isValidBettingModes(types)) {
            throw new Error("Provided invalid betting mode!");
        }

        this._types = types;
    },

    /**
     * Formats the cents.
     *
     * @param {number} cents The amount to format.
     * @param {Object} options The formatting options.
     * @param {string} [options.format='player'] The money formatting profile.
     * @param {number} [options.decimals=2] The number of decimals to show.
     * @param {boolean} [options.useCurrencySign=false] Whether or not to show the currency sign.
     * @returns {string} The formatted money.
     */
    format: function(cents, options) {
        var format = options.format || "player",
            decimals = typeof options.decimals === "number" ? options.decimals : 2;

        if (options.useCurrencySign) {
            return Services.moneyManager.formatMoneyCurrencySign(cents, format, decimals);
        }

        return Services.moneyManager.formatMoney(cents, format, decimals);
    },

    /**
     * Gets the currency format.
     *
     * @returns {CurrencyFormat} The currency format.
     */
    getCurrencyFormat: function() {
        var format = Resources.readData("moneyformat_player"),
            dividers = format.getElementsByTagName("dividers")[0],
            decimals = Number(
                format.getElementsByTagName("decimals")[0].childNodes[0]
                    .nodeValue
            ),
            iso = format.getElementsByTagName("iso")[0].childNodes[0].nodeValue;

        return {
            iso: iso,
            decimals: decimals,
            decimalDivider: dividers.getAttribute("decimal"),
            thousandsDivider: dividers.getAttribute("thousands")
        };
    },

    /**
     * Handles when the player balance has changed.
     *
     * @private
     * @param {Object} balance The balance object.
     * @param {number} balance.denomination The denomination.
     * @param {number} balance.playerBalanceCents The playerBalanceCents.
     * @param {number} balance.playerBalanceCoins The playerBalanceCoins.
     * @param {number} balance.playerBalanceFormatted The playerBalanceCoins.
     * @param {number} balance.playerBetCents The playerBetCents.
     * @returns {void}
     */
    _balanceChanged: function(balance) {
        this.publish("balanceChanged", balance);
    },

    /**
     * Retrieve Money types and defaultBetting Mode.
     *
     * @private
     * @param {Object} queryData - Query parameters info.
     * @param {Object} initResponse - The init response.
     * @returns {void}
     */
    _setupMoneyInfo: function(queryData, initResponse) {
        var operatorBettingModes = this._getOperatorBettingModes(queryData),
            sppBettingModes = this._getSppBettingModes(initResponse);

        this._setupMoneyConfig(operatorBettingModes, sppBettingModes);
    },

    /**
     * Method to retrieve spp config.
     *
     * @private
     * @param {Object} initResponse - The init response.
     * @returns {Array<string>} SppConfig.
     */
    _getSppBettingModes: function(initResponse) {
        var bettingModes;

        if (initResponse && initResponse.config && initResponse.config.gameParameters) {
            bettingModes = initResponse.config.gameParameters.bettingModes;
        }

        return bettingModes ? this._parseBettingModes(bettingModes) : undefined;
    },

    /**
     * Method to retrieve operator config.
     *
     * @param {Object} queryData - Query parameters info.
     * @private
     * @returns {Array<string>} Operator config.
     */
    _getOperatorBettingModes: function(queryData) {
        var bettingModes = queryData.bettingModes;

        return bettingModes ? this._parseBettingModes(bettingModes) : undefined;
    },

    /**
     * Set Money types based on the betting Mode.
     *
     * @private
     * @param {Array<string>} operatorBettingModes Object.
     * @param {Array<string>} sppBettingModes Object.
     * @returns {void}
     */
    _setupMoneyConfig: function(operatorBettingModes, sppBettingModes) {
        this._types = this._defaultMoneyTypes;

        if (operatorBettingModes && operatorBettingModes.length > 0) {
            this._types = operatorBettingModes;
        }
        else if (sppBettingModes && sppBettingModes.length > 0) {
            this._types = sppBettingModes;
        }

        this._type = this._types[0];
    },

    /**
     * Parses and validates if betting modes provided are a valid type of data.
     *
     * @param {Array<string> | string} bettingModes Provided value of bettingModes setting.
     * @returns {Array<string>} Parsed and validated betting mode;.
     * @private
     */
    _parseBettingModes: function(bettingModes) {
        if (!Array.isArray(bettingModes)) {
            bettingModes = [bettingModes];
        }

        if (!this._isValidBettingModes(bettingModes)) {
            this.observable.fireEvent(
                "request:loaderErrorHandler.showTechnicalError"
            );
            throw new Error("Provided invalid betting mode!");
        }

        return bettingModes;
    },

    /**
     * Checks if provided betting mode is a valid variant.
     *
     * @param {Array<string>} bettingModes Comma separated money types.
     * @private
     * @returns {boolean} Returns true if valid betting mode is provided.
     */
    _isValidBettingModes: function(bettingModes) {
        var correctLength = bettingModes.length > 0 && bettingModes.length < 3,
            possibleValues = ["cash", "coins"],
            correctValues = bettingModes.every(function(mode, index) {
                return possibleValues.indexOf(mode) > -1;
            }),
            sameValue = bettingModes[0] === bettingModes[1];

        return correctLength && !sameValue && correctValues;
    }
};

Services.Money = Sys.extend(Services.Base, Services.Money, "Services.Money");
